// src/pages/client/BookingSuccessPage.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { formatCurrency } from '../../utils/formatters';
import TeslaButton from '../../components/common/TeslaButton';
import ScrollReveal from '../../components/common/ScrollReveal';
import { CheckCircleIcon, CalendarIcon, TimeIcon, PersonIcon, ServiceIcon, ArrowBackIcon } from '../../components/common/Icons';
import { appointmentService } from '../../services/appointmentService';
import { Appointment } from '../../types/appointment';
import { publicAppointmentRef } from '../../utils/publicRefs';

interface BookingSuccessState {
  appointmentId: string;
  serviceDetails: {
    name: string;
    price: number;
    duration: number;
  };
  stylistDetails: {
    name: string;
    profilePhoto?: string;
  };
  appointmentDateTime: string;
  appointment?: Appointment; // Full appointment data if available
  isDownPayment?: boolean; // Flag indicating if this was just a down payment
  
  // Down payment calculation details
  downPaymentAmount?: number; // Amount that will be charged as down payment
  downPaymentPercentage?: number; // Resolved stylist down payment percentage
  remainingAmount?: number; // Amount to be paid later (75%)
  remainingPercentage?: number; // Percentage remaining (75%)
  totalAmount?: number; // Total service price
}

const BookingSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state as BookingSuccessState;
  const [fullAppointment, setFullAppointment] = useState<Appointment | null>(null);
  const [, setLoading] = useState(true);
  const [, setFetchError] = useState(false);
  
  useEffect(() => {
    // If no booking data, redirect to dashboard
    if (!bookingData) {
      navigate('/client/dashboard');
      return;
    }
    
    // Fetch full appointment data to get add-ons
    const fetchAppointment = async () => {
      try {
        setLoading(true);
        setFetchError(false);
        const appointment = await appointmentService.getAppointment(bookingData.appointmentId);
        setFullAppointment(appointment);
      } catch (error) {
        console.error('Failed to fetch appointment details:', error);
        setFetchError(true);
        // Continue to show page even if fetch fails - we have basic booking data
      } finally {
        setLoading(false);
      }
    };
    
    fetchAppointment();
  }, [bookingData, navigate]);
  
  if (!bookingData) {
    return null;
  }
  
  // Use fullAppointment data if available, fallback to bookingData.appointment
  const appointment = fullAppointment || bookingData.appointment;
  
  // Format the time for display - more robust to handle different formats
  const formatTime = (timeString: string): string => {
    try {
      let appointmentTime: Date;
      
      // Handle ISO format (with T and Z)
      if (timeString.includes('T') && timeString.includes('Z')) {
        appointmentTime = new Date(timeString);
      } 
      // Handle time strings (HH:MM format)
      else if (timeString.match(/^\d{1,2}:\d{2}$/)) {
        appointmentTime = new Date(`2000-01-01T${timeString}`);
      }
      // Try parsing as ISO
      else {
        appointmentTime = parseISO(timeString);
      }
      
      // Format the time
      return format(appointmentTime, 'h:mm a');
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return timeString; // Return original if we can't parse it
    }
  };
  
  // Format the date for display - more robust
  const formatDate = (dateTimeString: string): string => {
    try {
      let dateTime: Date;
      
      // If we have full appointment data, use that instead
      if (appointment?.dateTime) {
        dateTime = new Date(appointment.dateTime);
        return format(dateTime, 'EEEE, MMMM d, yyyy');
      }
      
      // Handle ISO format (with T and Z)
      if (dateTimeString.includes('T')) {
        dateTime = new Date(dateTimeString);
      } else {
        // Try parsing as ISO
        dateTime = parseISO(dateTimeString);
      }
      
      return format(dateTime, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', dateTimeString, error);
      return dateTimeString; // Return original if we can't parse it
    }
  };
  
  return (
    <div className="container mt-16 mb-16">
      <ScrollReveal animation="fade-in-up">
        <div className="card text-center">
          <div className="card__content">
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className="success-icon-wrapper">
                <CheckCircleIcon />
              </div>
            </div>
            
            <h1 className="heading-3 mb-4">Booking Confirmed!</h1>
            
            <p className="text-gray-60 mb-6">
              {bookingData.isDownPayment && (bookingData.downPaymentAmount ?? 0) > 0
                ? `Your appointment request has been submitted. Your card is saved — you will not be charged when the stylist confirms. A ${bookingData.downPaymentPercentage ?? 0}% booking guarantee of ${formatCurrency(bookingData.downPaymentAmount || 0)} may be charged if you cancel late or no-show. The full amount of ${formatCurrency(bookingData.totalAmount || 0)} is collected when your appointment is completed.`
                : `Your appointment request has been submitted. If the stylist confirms your appointment, the full amount of ${formatCurrency(bookingData.totalAmount || 0)} will be collected when your appointment is completed.`}
            </p>
            
            <hr className="divider my-6" />
            
            <div className="booking-details">
              <div className="booking-detail-item">
                <CalendarIcon />
                <span>{formatDate(bookingData.appointmentDateTime)}</span>
              </div>
              
              <div className="booking-detail-item">
                <TimeIcon />
                <span>{formatTime(bookingData.appointmentDateTime)}</span>
              </div>
              
              <div className="booking-detail-item">
                <ServiceIcon />
                <span>
                  {bookingData.serviceDetails.name} ({bookingData.serviceDetails.duration} min)
                </span>
              </div>
              
              <div className="booking-detail-item">
                <PersonIcon />
                <span>{bookingData.stylistDetails.name}</span>
              </div>
              
              <div className="booking-detail-item">
                <span>
                  {formatCurrency(appointment?.service?.finalPrice || bookingData.serviceDetails.price)}
                </span>
              </div>
              
              {/* Add-ons display */}
              {appointment?.addons && Array.isArray(appointment.addons) && appointment.addons.length > 0 && (
                <>
                  <div className="booking-detail-item">
                    <div className="add-icon">+</div>
                    <span>
                      Add-ons ({appointment.addons.length}): {formatCurrency(
                        appointment.addons.reduce(
                          (sum: number, addon: any) => {
                            const price = typeof addon?.priceCents === 'number' ? addon.priceCents / 100 : 0;
                            return sum + price;
                          },
                          0
                        )
                      )}
                    </span>
                  </div>
                  
                  <div className="booking-detail-item total-price">
                    <span style={{ fontWeight: 700, color: '#e82127' }}>
                      Total: {formatCurrency(
                        (appointment?.service?.finalPrice || bookingData.serviceDetails.price || 0) +
                        appointment.addons.reduce(
                          (sum: number, addon: any) => {
                            const price = typeof addon?.priceCents === 'number' ? addon.priceCents / 100 : 0;
                            return sum + price;
                          },
                          0
                        )
                      )}
                    </span>
                  </div>
                </>
              )}
              
              {appointment?.isMobileService && (
                <div className="booking-detail-item mobile-service">
                  <div className="home-icon">🏠</div>
                  <span>In-Home Service</span>
                </div>
              )}
            </div>
            
            <hr className="divider my-6" />
            
            <p className="mb-8">
              We've sent a confirmation email with all the details.
              {appointment?.service?.finalPrice !== bookingData.serviceDetails.price && (
                <span className="loyalty-applied">
                  {" "}Loyalty points have been applied to this booking.
                </span>
              )}
            </p>
            
            <div className="flex justify-center gap-4">
              <TeslaButton
                variant="outline"
                icon={<ArrowBackIcon />}
                onClick={() => navigate('/client/dashboard')}
              >
                Back to Dashboard
              </TeslaButton>
              
              <TeslaButton
                variant="primary"
                icon={<CalendarIcon />}
                onClick={() => navigate(`/client/appointment/${publicAppointmentRef(appointment) || bookingData.appointmentId}`)}
              >
                View Appointment
              </TeslaButton>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>
  );
};

export default BookingSuccessPage;